import React, { useState, useContext, useEffect } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Form,
  Space,
  Card,
  Steps,
  Result,
} from "antd";
import { UserOutlined, EditOutlined, DeleteOutlined, RightOutlined } from "@ant-design/icons";
import { useStepsForm } from "sunflower-antd";

import { AuthContext } from "../../../context/AuthContext";
import { useFormContext } from "../../../hooks/useFormContext";
import { toast } from "../../../utils/toast";
import { getCompanyProductsApi } from "../../../services/Product/ProductApis";

import PersonalInfo from "../../FormInputs/PersonalInfo";
import AdditionalInfo from "../../FormInputs/AdditionalInfo";
import ResidentialInfo from "../../FormInputs/ResidentialInfo";
import ProductInfo from "../../FormInputs/ProductInfo";
import CoveredLives from "../../FormInputs/CoveredLives";
import Beneficiary from "../../FormInputs/Beneficiary";
import Addon from "../../FormInputs/Addon";
import Documents from "../../FormInputs/Documents";
import PaymentMethodStep from "../../FormInputs/PaymentMethodStep";
import {
  addMemberDocumentApi,
  generateDocumentApi,
} from "../../../services/Document/DocumentApis";
import { getGroupApi } from "../../../services/Group/GroupApis";
import { addTransactionApi } from "../../../services/Transaction/TransactionApis";
import { useGuardedModalClose } from "../../../hooks/useGuardedModalClose";
import dayjs from "dayjs";
import { toAddonsPayload } from "../../../utils/addonHelpers";
import { calculateAge } from "../../../utils/ageHelper";
import { toMemberApiInceptionDate } from "../../../utils/sastDate";
import { invalidateCompliance } from "../../../utils/complianceInvalidate";
import WizardStepLayout from "../../FormInputs/WizardStepLayout";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleAdd: Function;
}

const AddMemberModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleAdd,
}) => {
  const { currentGroup } = useContext(AuthContext);
  const [memberId, setMemberId] = useState(0);
  const {
    personalInfo,
    productInfo,
    setProductInfo,
    coveredLive,
    setCoveredLive,
    coveredProductInfo,
    setCoveredProductInfo,
  } = useFormContext();
  const [applicationDocument, setApplicationDocument] = useState("");
  const [policyDocument, setPolicyDocument] = useState("");
  const [cardData, setCardData] = useState<any>([]);
  const [memberAge, setmemberAge] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(0);

  const handleAge = (date: Date | string) => {
    const age = calculateAge(date);
    if (age != null) {
      setmemberAge(age);
    }
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    defaultFormValues: {
      CoveredAmount: productInfo.CoverAmount,
    },
    async submit(values: any) {
      setLoading(true);

      try {
        const {
          CoveredName,
          CoveredSurname,
          CoveredIdNumber,
          CoveredDateOfBirth,
          CoveredType,
          CoveredAmount,
          ExtendedProduct,
          ...newRequestBody
        } = values;

        // Prefer a numeric group id — list endpoint is /member/group/:groupId
        const groupId = Number(
          typeof currentGroup === "object" && currentGroup != null
            ? (currentGroup as any).id
            : currentGroup
        );
        if (!groupId || Number.isNaN(groupId)) {
          setErrorMsg(
            "No group selected. Please select a group (e.g. Simon Dithebe Funerals) and try again."
          );
          return false;
        }

        const productTypeName =
          typeof values.ProductType === "object"
            ? String(values.ProductType?.label ?? values.ProductType?.value ?? "")
            : String(values.ProductType ?? "");
        if (productTypeName !== "One Plus") {
          newRequestBody.ExtendedAge = null;
        }

        newRequestBody.Addons = toAddonsPayload(values.Addons);

        const response = await handleAdd({
          ...newRequestBody,
          InceptionDate: toMemberApiInceptionDate(newRequestBody.InceptionDate),
          // Send both shapes so the backend persists the group association
          Group: groupId,
          GroupID: groupId,
          CoveredLive: cardData,
        });

        // handleAdd may return undefined if its internal catch fires
        if (!response) {
          setErrorMsg("An unexpected error occurred. Please try again.");
          return false;
        }

        if (response?.success && response?.data?.id) {
          // Add first premium transaction
          setMemberId(response.data.id);
          setProductInfo({});
          setCardData([]);

          // Policy may be null if backend policy creation failed — skip transaction silently
          if (response.data.Policy?.id) {
            await addTransactionApi(response.data.Policy.id, {
              Amount: `${-response.data.TotalPremium}`,
              Type: "System",
              Reference: "Premium Payable",
              Note: "Monthly Premium",
              IsSuccessful: "SUCCESSFUL",
              Date: dayjs(Date.now()),
              PaymentProvider: "System",
            });
          } else {
            console.warn("Member created but Policy was null — transaction skipped.");
          }

          return true;
        } else {
          setErrorMsg(response?.message || response?.data?.message || "An unknown error occurred.");
          return false;
        }
      } catch (error) {
        console.error("Error during submission:", error);
        setErrorMsg("Failed to submit form. Please try again.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    total: 11,
  });

  const resetModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const { requestClose, onValuesChange, markModalSaved } = useGuardedModalClose({
    id: "add-member-modal",
    open: show,
    hasChanges: current > 0 && current < 9,
    onClose: resetModal,
  });

  const handleModal = () => {
    void requestClose();
  };

  const handleCompletedClose = () => {
    markModalSaved();
    resetModal();
  };

  const handleGenerateDocument = async (id: string, type: string) => {
    setLoading(true);

    return await generateDocumentApi(id, type, Number(memberId))
      .then((res) => {
        console.log(res);
        setLoading(false);
        return res;
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleUploadID = async (values: any): Promise<boolean> => {
    try {
      setLoading(true);

      const response = await addMemberDocumentApi(Number(memberId), {
        base64: values.base64,
        name: values.name,
        documentType: values.documentType,
      });

      if (!response.success) {
        // Non-fatal: document may have saved but shortlink/SMS step failed.
        // Warn and continue rather than crashing the whole process.
        console.warn("Document upload returned non-success (possibly SMS/shortlink):", response.message);
        toast.warning({
          title: "Document uploaded",
          description: "Document was saved but the SMS notification could not be sent.",
        });
        return true;
      }

      console.log(
        "Successfully uploaded, generating application and policy documents..."
      );

      // Generate both documents in parallel if they are independent
      await Promise.all([
        handleGenerateDocument(applicationDocument, "Document"),
        handleGenerateDocument(policyDocument, "Document"),
      ]);

      invalidateCompliance();

      return true;
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMsg("An unexpected error occurred while uploading.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoveredLive = (data: any) => {
    const values = form.getFieldsValue();

    const newItem = {
      CoveredName: data.CoveredName,
      CoveredSurname: data.CoveredSurname,
      CoveredIdNumber: data.CoveredIdNumber,
      CoveredDateOfBirth: data.CoveredDateOfBirth,
      CoveredType: data.CoveredType,
      ExtendedProduct:
        typeof coveredProductInfo === "number" ? coveredProductInfo : null,
    };

    // Reset specific fields
    form.setFieldsValue({
      ...values,
      CoveredName: "",
      CoveredSurname: "",
      CoveredIdNumber: "",
      CoveredDateOfBirth: "",
      CoveredType: "",
      ExtendedProduct: "",
    });

    setCoveredProductInfo(null);
    setCardData((prev: any) => [...prev, newItem]);
  };
  
  const handleEditCoveredLive = (data: any, index: any) => {
    setIsEditing(true);
    setCoveredLive({ ...data });
    setEditingIndex(index);
  };

  const handleSaveCoveredLive = (data: any) => {
    const values = form.getFieldsValue();
    const updatedCardData = [...cardData];
    updatedCardData[editingIndex] = {
      CoveredType: data.CoveredType,
      CoveredName: data.CoveredName,
      CoveredSurname: data.CoveredSurname,
      CoveredIdNumber: data.CoveredIdNumber,
      CoveredDateOfBirth: data.CoveredDateOfBirth,
      ExtendedProduct: data.CoveredType === "Extended" ? coveredProductInfo : null,
    };
    form.setFieldsValue({
      ...values,
      CoveredName: "",
      CoveredSurname: "",
      CoveredIdNumber: "",
      CoveredDateOfBirth: "",
      CoveredType: "",
      ExtendedProduct: "",
    });
    setCardData(updatedCardData);
    setIsEditing(false);
    setCoveredLive({});
  };

  const handleRemoveCoveredLive = (indexToRemove: number) => {
    const updatedCardData = cardData.filter(
      (_: any, i: number) => i !== indexToRemove
    );
    setCardData(updatedCardData);
  };

  useEffect(() => {
    if (show && currentGroup) {
      // Check for products when modal opens
      getCompanyProductsApi(String(currentGroup))
        .then((res) => {
          if (!res.data || res.data.length === 0) {
            toast.warning({
              title: 'No Products Configured',
              description: 'This group has no products set up. Please configure products before capturing policies.',
              duration: 8,
            });
          }
        })
        .catch((err) => {
          console.error('Error checking products:', err);
        });

      getGroupApi(String(currentGroup))
        .then((res) => {
          setApplicationDocument(res.data.ApplicationDocument);
          setPolicyDocument(res.data.PolicyDocument);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [show, currentGroup]);

  // Step Styling
  const layout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 16 },
    labelAlign: "right" as const,
  };

  const coveredLivesTailLayout = {
    wrapperCol: { offset: 7, span: 16 },
  };

  const display: any = [
    <PersonalInfo
      form={form}
      handleAge={handleAge}
      gotoStep={gotoStep}
      current={current}
    />,

    <AdditionalInfo
      memberAge={memberAge}
      gotoStep={gotoStep}
      current={current}
      form={form}
    />,

    <ResidentialInfo gotoStep={gotoStep} current={current} form={form} />,

    <ProductInfo
      memberAge={memberAge}
      gotoStep={gotoStep}
      current={current}
      form={form}
    />,

    <WizardStepLayout
      action={
        <Form.Item
          labelCol={{ span: 8 }}
          wrapperCol={{ offset: 8, span: 8 }}
          style={{ marginBottom: 0 }}
        >
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Form.Item>
      }
    >
      <Row gutter={16} style={{ minWidth: 0 }}>
        <Col span={14} style={{ borderRight: "1px solid #f0f0f0", paddingRight: 16, minWidth: 0 }}>
          <CoveredLives
            gotoStep={gotoStep}
            current={current}
            form={form}
            handleAge={handleAge}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {isEditing ? (
              <Button
                type="primary"
                size="large"
                block
                onClick={() => {
                  const formData = form.getFieldsValue();
                  handleSaveCoveredLive(formData);
                }}
              >
                Save
              </Button>
            ) : (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  type="primary"
                  size="large"
                  style={{ width: "50%" }}
                  onClick={() => {
                    const formData = form.getFieldsValue();
                    handleAddCoveredLive(formData);
                  }}
                >
                  Add <RightOutlined />
                </Button>
              </div>
            )}
          </div>
        </Col>
        <Col span={10} style={{ minWidth: 0 }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Card
              key={1}
              title={
                <>
                  {" "}
                  <UserOutlined />{" "}
                  <span>
                    {personalInfo.Name} {personalInfo.Surname}
                  </span>
                </>
              }
              extra={"Main"}
              style={{ marginBottom: 16, backgroundColor: "#E0E0E0" }}
            ></Card>
            {cardData.map((item: any, index: number) => (
              <Card
                key={index}
                title={
                  <>
                    {" "}
                    <UserOutlined />{" "}
                    <span>
                      {item.CoveredName} {item.CoveredSurname}
                    </span>
                  </>
                }
                style={{ marginBottom: 16 }}
                hoverable={true}
                extra={item.CoveredType}
              >
                <Space>
                  <EditOutlined
                    style={{ color: "green" }}
                    onClick={() => handleEditCoveredLive(item, index)}
                  />
                  <DeleteOutlined
                    style={{ color: "red" }}
                    onClick={() => handleRemoveCoveredLive(index)}
                  />
                </Space>
              </Card>
            ))}
          </div>
        </Col>
      </Row>
    </WizardStepLayout>,

    <Beneficiary
      gotoStep={gotoStep}
      current={current}
      handleAge={handleAge}
      form={form}
      coveredLives={cardData}
    />,

    <Addon
      loading={loading}
      gotoStep={gotoStep}
      current={current}
      submit={submit}
      form={form}
      actionLabel="Next"
      failStep={10}
    />,

    <PaymentMethodStep
      form={form}
      memberId={memberId}
      gotoStep={gotoStep}
      documentsStep={8}
    />,

    <Documents
      loading={loading}
      gotoStep={gotoStep}
      current={current}
      submit={(values: any) => handleUploadID(values)}
    />,
  ];

  return (
    <Modal
      title="New Member"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={1000}
      footer={null}
      styles={{ body: { overflowX: "hidden" } }}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24, minWidth: 0 }}>
        {current !== 9 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Personal Info" />
              <Step title="Additional Info" />
              <Step title="Residential Info" />
              <Step title="Product Info" />
              <Step title="Covered Lives" />
              <Step title="Beneficiary" />
              <Step title="Addons" />
              <Step title="Payment Method" />
              <Step title="ID Document" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, marginTop: 24, overflowX: "hidden" }}>
          <Form
            {...layout}
            {...formProps}
            style={{ width: "100%", maxWidth: "none" }}
            onValuesChange={onValuesChange}
          >
            {display[current]}
          </Form>

          {current === 9 && (
            <Result
              status="success"
              title="Successfully submitted Member, ID Document and generated supporting documents."
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 10 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddMemberModal;
